import React from 'react';
import AuthLayout from '../login/AuthLayout';
import Loader from '../../components/shared/Loader.jsx';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import litterals from 'litterals';
import { useTranslation } from 'react-i18next';
import api from 'api';
import { getTypeSaml } from 'utils/saml';

const SamlCallback = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { type } = useParams();

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [, setCompany] = useState({});
  const [error, setError] = useState('');

  const submitSamlToken = useMutation(api.Auth.samlAuthTokenCallback, {
    onSuccess: async (data) => {
      localStorage.removeItem('SAML_TOKEN_AUTH');
      localStorage.setItem('REACT_TOKEN_AUTH', data.token);
      localStorage.setItem('REACT_REFRESH_TOKEN_AUTH', data.refreshToken);

      await queryClient.invalidateQueries();
      const redirectAuth = localStorage.getItem('REDIRECT_AUTH');
      if (redirectAuth) {
        setCompany(data.user.company);
        localStorage.removeItem('REDIRECT_AUTH');
        window.location = redirectAuth;
      } else {
        if (data.user.acceptCompanyTerms) return navigate('/home');
        setCompany(data.user.company);
      }
    },
    onError: (err) => {
      localStorage.removeItem('SAML_TOKEN_AUTH');

      if (!error && err?.response?.data?.error) {
        if (err?.response?.data?.code === 500) {
          setError(t('saml_auth_unknown_error'));
        } else {
          const { code } = err.response.data;
          if (code) {
            setError(t(`saml_auth_server_${code}_error`));
          } else {
            setError(err.response.data.error);
          }
        }
      }
    },
  });

  useEffect(() => {
    if (searchParams.has('token')) {
      const token = searchParams.get('token');
      searchParams.delete('token');
      setSearchParams(searchParams);

      const ssoType = getTypeSaml(type);

      if (ssoType) {
        submitSamlToken.mutate({ token, type: ssoType });
      } else {
        setError(t('saml_auth_server_27001_error'));
      }
    } else if (!error) {
      setError(initError());
    }
  }, []);

  const initError = () => {
    if (searchParams.has('error')) {
      const codeError = searchParams.get('error');
      const existingTranslation = i18n.exists(`saml_auth_${codeError.toLowerCase()}_error`);

      if (existingTranslation) {
        return t(`saml_auth_${codeError.toLowerCase()}_error`);
      }
    } else if (!searchParams.has('token')) {
      return t('saml_auth_server_no_code_error');
    }

    return;
  };

  const displayError = (error) => {
    if (!error) return;

    return (
      <Box sx={{ margin: 'auto' }}>
        <Typography
          gutterBottom
          component="h4"
          color="red"
          fontSize={'16px'}
          marginLeft={'12px'}
          lineHeight={'24px'}
          fontWeight={400}
          letterSpacing={'0.15px'}>
          {error}
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Button onClick={() => navigate('/login')} variant="contained">
            {t(litterals.MICROSOFT_AUTH_BUTTON)}
          </Button>
        </Box>
      </Box>
    );
  };

  const displayLoader = () => {
    if (error) return;

    return (
      <Box sx={{ margin: 'auto' }}>
        <Loader show />
      </Box>
    );
  };

  return (
    <AuthLayout>
      {displayError(error)}
      {displayLoader(error)}
    </AuthLayout>
  );
};

export default SamlCallback;
